import { type ComponentProps, useState } from "react";
import {
    CloseHealth,
    CloseHealthReason,
    closeHealthLabels,
    closeHealthReasonDescriptions,
} from "@common/types/closeHealth";
import {
    HealthIndicator,
    type IndicatedHealth,
} from "@design-system/HealthIndicator/HealthIndicator";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";
import { PlaygroundSelect } from "./PlaygroundSelect";
import { TextControl, VariantGrid, VariantItem } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import gridStyles from "./playgroundControls.module.scss";

type HealthIndicatorSize = NonNullable<
    ComponentProps<typeof HealthIndicator>["size"]
>;

const healths = Object.values(CloseHealth).filter(
    (health): health is IndicatedHealth => health !== CloseHealth.NONE,
);

const healthOptions = healths.map((health) => ({
    value: health,
    label: closeHealthLabels[health],
}));

const sizes: readonly HealthIndicatorSize[] = ["md", "sm"];

const sizeOptions = sizes.map((size) => ({ value: size, label: size }));

/** Design-system playground for the Work page's engagement health chip. */
export const HealthIndicatorPlayground = () => {
    const [health, setHealth] = useState<IndicatedHealth>(CloseHealth.AT_RISK);
    const [size, setSize] = useState<HealthIndicatorSize>("md");
    const [reason, setReason] = useState(
        closeHealthReasonDescriptions[CloseHealthReason.DELIVERY_DATE_PASSED],
    );

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Health</h2>
                        <PlaygroundSelect<IndicatedHealth>
                            options={healthOptions}
                            value={health}
                            onChange={setHealth}
                        />
                    </section>
                    <section>
                        <h2>Size</h2>
                        <PlaygroundSelect<HealthIndicatorSize>
                            options={sizeOptions}
                            value={size}
                            onChange={setSize}
                        />
                    </section>
                    <section>
                        <h2>Tooltip</h2>
                        <TextControl
                            label="Reason"
                            value={reason}
                            onChange={setReason}
                        />
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={closeHealthLabels[health]}
                width="full"
                output={JSON.stringify({ health, size }, null, 2)}
            >
                <VStack gap={16} className={gridStyles.fillWidth}>
                    <HStack justify="center">
                        <Tooltip content={reason || undefined}>
                            <HealthIndicator health={health} size={size} />
                        </Tooltip>
                    </HStack>
                    <VariantGrid title="Health" columns="auto">
                        {healths.map((option) => (
                            <VariantItem key={option} label={option}>
                                <HealthIndicator health={option} />
                            </VariantItem>
                        ))}
                    </VariantGrid>
                    <VariantGrid title="Size" columns="auto">
                        {sizes.map((option) => (
                            <VariantItem key={option} label={option}>
                                <HealthIndicator
                                    health={health}
                                    size={option}
                                />
                            </VariantItem>
                        ))}
                    </VariantGrid>
                </VStack>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
